import { readFile } from 'node:fs/promises';
import { join, resolve } from 'node:path';

import {
  loadStackChoicesFile,
  resolveWorkspaceRoot,
  saveStackChoicesFile
} from './config';

import { runProjectScan } from './runScan';

import { writeScanArtifacts, type WriteArtifactsResult } from './artifacts';
import type { GateResult } from './contracts/gateResult';
import { renderGateResultJson } from './output/json';
import { renderJsonlEvents } from './output/jsonl';
import { exitCodeForStrictGate } from './commands/strictGate';

import { refreshReportFromDisk } from './report/refreshReport';

import { openPathInBrowser } from './openBrowser';

import { copyToClipboard } from './clipboard';
import { printScanSummary } from './terminalSummary';
import { ERROR, formatAgentStatus } from './statusLabels';
import { sanitizeArtifactForDisk } from './sanitizeArtifact';

import { isScanNotFoundError, loadLastArtifact, pickGap } from './tui/menu';

import { runInteractiveSession } from './tui/runInteractive';

import { runGuideCommand } from './commands/guide';
import { runNextCommand } from './commands/next';
import { runOpenCommand } from './commands/open';
import { runCondenseCommand } from './commands/condense';
import { runHealCommand } from './commands/heal';
import { resolveNextAction } from './resolveNextAction';
import { recommendStack } from './stackRecommend';

import { PUBLIC_AGENT_MODE_COMMAND, PUBLIC_INIT_ALL_COMMAND } from './contracts/commands';
import { runInitCommand } from './commands/runInit';
import { runDoctorAgentsCommand } from './commands/runDoctor';
import { runValidateNpmPackageCommand } from './commands/runValidateNpmPackage';
import { runAuditCommand } from './commands/runAudit';
import { runActionsCommand } from './commands/actions';
import { runVerifyActionCommand } from './commands/verifyAction';
import { runPreviewCommand } from './commands/preview';
import { runCheckCommand } from './commands/check';
import { startLocalUiServer, waitForServerShutdown } from './local-ui/server';
import { VERSION } from './version';
import { loadLoopState, saveLoopState, resetBatch } from './loopState';
import {
  buildNextActionBlock,
  printNextActionBlock,
  printProviderActionBlock,
} from './output/nextActionBlock';
import { buildTaskList } from './buildTaskList';
import { verifyProviderGap } from './providerMcpBridge';
import { renderActionSurface } from './actions/render';
import type { VibeRavenActionsManifest } from './actions/types';



export function printHelp(): void {

  console.log(`viberaven ${VERSION} — the control layer for AI-built products. Local, offline, no login.

Usage:

  viberaven              Open the Studio (local launch console)

  viberaven ui [path]    Open the Studio

  viberaven tui          Interactive terminal menu

  viberaven check [--json] [--quiet] [path]
                       Local production check: one line per finding, exit 1 on blockers

  viberaven fix [--gap <id>] [path]
                       Apply a safe automated fix for a known gap (heal recipe)

  viberaven scan [--open] [--json] [path]
                       Full local scan; writes .viberaven/ artifacts

  viberaven --agent-mode [--json|--jsonl] [path]
                       Agent-first scan; writes tasklist, gate-result, context-map, and per-gap JSON

  viberaven --strict[=warning] [path]
                       Fail when production gate is not clear; warning mode also fails on warnings

  viberaven --condense [path]
                       Refresh .viberaven/context-map.json from the last scan

  viberaven status [--json] [path]
                       Verdict from the last local scan

  viberaven actions [--json] [path]
                       Print current chat-native production action surface

  viberaven report [--open] [path]
                       Rebuild report.html from last scan (no new scan)

  viberaven prompt [--gap <id>] [--provider <key>] [--area <key>] [--no-copy]

  viberaven stack set <area> <provider>

  viberaven stack clear <area>

  viberaven stack list

  viberaven stack recommend
                       Suggest agent-default stack from package.json

  viberaven next [--json] [path]
                       Next action from last scan (repo fix or provider guide)

  viberaven guide <provider> [--step N] [--json]
                       Provider setup wizard (vercel, supabase, stripe, auth-supabase)

  viberaven open [provider|url]
                       Open dashboard URL from next action or playbook

  viberaven init [--agents all|codex,claude,...] [--dry-run] [path]
                       Install bounded VibeRaven agent rules (${PUBLIC_INIT_ALL_COMMAND})

  viberaven doctor --agents [path]
                       Verify agent instruction files and canonical commands

  viberaven audit --vercel-supabase [--json] [path]
                       Local Vercel/Supabase repo evidence audit (RLS, pooler, secrets)



Agent workflow (Claude Code / Codex / Gemini):

  ${PUBLIC_AGENT_MODE_COMMAND}

  Read .viberaven/agent-tasklist.md first, .viberaven/gate-result.json for the machine verdict, then .viberaven/context-map.json

  viberaven next --json → fix → check again



Humans: run \`viberaven\` for the Studio, or \`viberaven check\` in any terminal.

Agents & CI: use \`viberaven check\` or \`${PUBLIC_AGENT_MODE_COMMAND}\`.



Everything runs locally against your repo. No account, no API key, no telemetry.

`);

}



export function parseArgs(argv: string[]): {

  command: string;

  flags: Record<string, string | boolean>;

  positional: string[];

} {

  const flags: Record<string, string | boolean> = {};

  const positional: string[] = [];

  let command = '';



  for (let i = 0; i < argv.length; i += 1) {

    const arg = argv[i];

    if (arg === '--help' || arg === '-h') {

      flags.help = true;

      continue;

    }

    if (arg === '--version' || arg === '-v') {

      flags.version = true;

      continue;

    }

    if (arg.startsWith('--')) {

      const equalsIndex = arg.indexOf('=');

      const key = equalsIndex === -1 ? arg.slice(2) : arg.slice(2, equalsIndex);

      if (equalsIndex !== -1) {

        flags[key] = arg.slice(equalsIndex + 1);

        continue;

      }

      const next = argv[i + 1];

      if (next && !isBooleanFlag(command, key) && !next.startsWith('-')) {

        flags[key] = next;

        i += 1;

      } else {

        flags[key] = true;

      }

      continue;

    }

    if (!command) {

      if (looksLikePath(arg)) {
        positional.push(arg);
      } else {
        command = arg;
      }

    } else {

      positional.push(arg);

    }

  }



  return { command, flags, positional };

}

/** A bare argument that looks like a filesystem path is a positional, not a command. */
function looksLikePath(arg: string): boolean {
  return /[\\/]/.test(arg) || arg === '.' || arg === '..' || /^[A-Za-z]:/.test(arg);
}

function isBooleanFlag(command: string, key: string): boolean {
  if ([
    'agent-mode',
    'json',
    'jsonl',
    'condense',
    'heal',
    'plan',
    'prompt',
    'apply',
    'yes',
    'no-verify',
    'force-scan',
  ].includes(key)) {
    return true;
  }
  if (key === 'strict') return true;
  if (key === 'open' && (command === '' || command === 'scan' || command === 'report')) return true;
  if (key === 'verify' && command === '') return true;
  if (key === 'quiet' && command === 'check') return true;
  if (key === 'vercel-supabase' && command === 'audit') return true;
  if (key === 'json' && command === 'validate-npm-package') return true;
  if (key === 'dry-run' && command === 'init') return true;
  if (key === 'agents' && command === 'doctor') return true;
  return false;
}

function hasFlag(flags: Record<string, string | boolean>, key: string): boolean {
  return flags[key] === true || typeof flags[key] === 'string';
}

export type DefaultEntrypointMode = 'local-ui' | 'interactive' | 'agent-scan';

export function resolveDefaultEntrypointMode(options: {
  stdinIsTTY: boolean;
  stdoutIsTTY: boolean;
  env: Record<string, string | undefined>;
}): DefaultEntrypointMode {
  if (options.env.VIBERAVEN_TUI === '1') return 'interactive';
  if (options.env.VIBERAVEN_AGENT === '1') return 'agent-scan';
  return 'local-ui';
}

export type RunScanCommandResult = {
  exitCode: number;
  artifacts?: WriteArtifactsResult;
};

export function formatScanJsonStdout(artifact: Parameters<typeof sanitizeArtifactForDisk>[0]): string {

  return JSON.stringify(sanitizeArtifactForDisk(artifact), null, 2);

}



async function cmdProviderVerify(
  flags: Record<string, string | boolean>,
  positional: string[]
): Promise<number> {
  const provider = typeof flags.provider === 'string' ? flags.provider : positional[0];
  const check = typeof flags.check === 'string' ? flags.check : positional[1];

  if (!provider || !check) {
    console.error('Usage: viberaven provider-verify --provider <supabase|vercel> --check <id>');
    return 1;
  }

  const result = await verifyProviderGap({
    provider,
    check,
    cwd: process.cwd(),
  });

  console.log(JSON.stringify(result, null, 2));
  return result.verified ? 0 : 1;
}



async function cmdStatus(
  flags: Record<string, string | boolean>,
  positional: string[]
): Promise<number> {

  const startDir = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();
  let artifact: Awaited<ReturnType<typeof loadLastArtifact>> | undefined;
  try {
    artifact = await loadLastArtifact(startDir);
  } catch {
    artifact = undefined;
  }

  if (!artifact) {
    console.log('No local scan yet. Run: viberaven check');
    return 1;
  }

  const next = resolveNextAction(artifact);

  if (flags.json) {
    console.log(
      JSON.stringify(
        {
          scannedAt: artifact.scannedAt,
          score: artifact.score,
          scoreLabel: artifact.scoreLabel,
          productionCorePercent: artifact.productionCorePercent,
          openGaps: artifact.gaps.length,
          next: next ?? null
        },
        null,
        2
      )
    );
    return 0;
  }

  console.log(`Last scan: ${artifact.scannedAt}`);
  console.log(`Production core: ${artifact.productionCorePercent}% · Score ${artifact.score} (${artifact.scoreLabel})`);
  console.log(`Open gaps: ${artifact.gaps.length}`);
  if (next) {
    console.log(`Next: ${next.title}`);
  }
  console.log('Re-check: viberaven check');

  return 0;

}



export async function runScanCommand(

  flags: Record<string, string | boolean>,

  positional: string[],

  options?: { deferMachineOutput?: boolean }

): Promise<RunScanCommandResult> {

  const workspacePath = positional[0]
    ? resolve(process.cwd(), positional[0])
    : await resolveWorkspaceRoot(process.cwd());

  if (!options?.deferMachineOutput) {
    console.log(`Scanning ${workspacePath}… (local, offline)`);
  }

  const result = await runProjectScan({ workspacePath });

  if (!result.ok) {

    console.error(formatAgentStatus(ERROR, result.message));

    return { exitCode: 1 };

  }

  const artifact = result.artifact;

  const paths = await writeScanArtifacts({ artifact, cwd: workspacePath });



  if (flags.json && !options?.deferMachineOutput) {

    console.log(formatScanJsonStdout(artifact));

    return { exitCode: 0, artifacts: paths };

  }

  if (!options?.deferMachineOutput) {
    printScanSummary(artifact, paths);
  }

  if (flags['agent-mode'] && !options?.deferMachineOutput) {
    const loopState = await loadLoopState(workspacePath);
    const openGapCount = artifact.gaps.length;
    const updatedState = resetBatch(loopState, openGapCount);
    const tasks = buildTaskList(artifact);
    const block = buildNextActionBlock(tasks, updatedState);
    printNextActionBlock(block);
    printProviderActionBlock(tasks);
    try {
      const manifest = JSON.parse(await readFile(paths.actionsPath, 'utf8')) as VibeRavenActionsManifest;
      console.log(renderActionSurface(manifest, { limit: 5 }).trimEnd());
    } catch (error) {
      console.warn(error instanceof Error ? error.message : String(error));
    }
    await saveLoopState(workspacePath, updatedState);
  }

  if (flags.open) {

    try {

      await openPathInBrowser(paths.reportPath);

    } catch (error) {

      console.warn(error instanceof Error ? error.message : String(error));

    }

  }



  return { exitCode: 0, artifacts: paths };

}



async function cmdReport(
  flags: Record<string, string | boolean>,
  positional: string[]
): Promise<number> {
  const startDir = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();

  try {
    const paths = await refreshReportFromDisk(startDir);
    console.log(`Report refreshed: ${paths.reportPath}`);

    if (flags.open) {
      try {
        await openPathInBrowser(paths.reportPath);
      } catch (error) {
        console.warn(error instanceof Error ? error.message : String(error));
      }
    }

    return 0;
  } catch (error) {
    if (isScanNotFoundError(error)) {
      console.error(error.message);
      return 1;
    }
    console.error(error instanceof Error ? error.message : String(error));
    return 1;
  }
}

async function cmdPrompt(

  flags: Record<string, string | boolean>,

  positional: string[]

): Promise<number> {

  const startDir = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();

  let artifact;

  try {

    artifact = await loadLastArtifact(startDir);

  } catch (error) {

    console.error(error instanceof Error ? error.message : 'No scan found. Run: viberaven scan');

    return 1;

  }



  const gap = pickGap(artifact, {

    gapId: typeof flags.gap === 'string' ? flags.gap : undefined,

    provider: typeof flags.provider === 'string' ? flags.provider : undefined,

    area: typeof flags.area === 'string' ? flags.area : undefined

  });



  if (!gap) {

    console.error('No matching gap. Run `viberaven scan` or pass --gap <id>.');

    return 1;

  }



  const skipCopy = flags['no-copy'] === true;

  if (!skipCopy) {
    try {
      await copyToClipboard(gap.copyPrompt);
      console.log(`Copied to clipboard: ${gap.title}`);
      return 0;
    } catch (error) {
      console.warn(error instanceof Error ? error.message : String(error));
    }
  }

  console.log(gap.copyPrompt);

  return 0;

}



const STACK_AREAS = new Set(['database', 'auth', 'payments', 'deployment', 'monitoring', 'security']);



async function cmdStack(positional: string[]): Promise<number> {

  const cwd = process.cwd();

  const sub = positional[0];



  if (sub === 'recommend') {
    const rec = await recommendStack(cwd);
    console.log(JSON.stringify(rec, null, 2));
    return 0;
  }

  if (sub === 'list' || !sub) {

    const file = await loadStackChoicesFile(cwd);

    const entries = Object.entries(file.choices);

    if (entries.length === 0) {

      console.log('No provider overrides. Detection runs from repo evidence.');

      return 0;

    }

    for (const [area, choice] of entries) {

      console.log(`${area}: ${choice.provider}`);

    }

    return 0;

  }



  if (sub === 'set') {

    const area = positional[1];

    const provider = positional[2];

    if (!area || !provider) {

      console.error('Usage: viberaven stack set <area> <provider>');

      return 1;

    }

    if (!STACK_AREAS.has(area)) {

      console.error(`Unknown area "${area}". Valid: ${[...STACK_AREAS].join(', ')}`);

      return 1;

    }

    const file = await loadStackChoicesFile(cwd);

    file.choices[area] = { provider, selectedAt: new Date().toISOString() };

    await saveStackChoicesFile(cwd, file);

    console.log(`Set ${area} → ${provider}. Run \`viberaven scan\` to re-map.`);

    return 0;

  }



  if (sub === 'clear') {

    const area = positional[1];

    const file = await loadStackChoicesFile(cwd);

    if (area) {

      delete file.choices[area];

    } else {

      file.choices = {};

    }

    await saveStackChoicesFile(cwd, file);

    console.log(area ? `Cleared ${area}.` : 'Cleared all provider overrides.');

    return 0;

  }



  console.error(`Unknown stack subcommand "${sub}". Use set, clear, or list.`);

  return 1;

}



export async function main(): Promise<number> {

  const { command, flags, positional } = parseArgs(process.argv.slice(2));



  if (flags.help) {

    printHelp();

    return 0;

  }

  if (flags.version || command === 'version') {

    console.log(VERSION);

    return 0;

  }

  const isAgentMode = hasFlag(flags, 'agent-mode');
  const wantsJson = hasFlag(flags, 'json');
  const wantsJsonl = hasFlag(flags, 'jsonl');
  const wantsStrict = hasFlag(flags, 'strict');

  if (flags.condense) {
    const cwd = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();
    const result = await runCondenseCommand({ cwd });
    console.log(`VibeRaven context map refreshed: ${result.contextMapPath}`);
    return 0;
  }

  if (flags.heal) {
    const mode = flags.apply ? 'apply' : flags.prompt ? 'prompt' : 'plan';
    const result = await runHealCommand({
      cwd: process.cwd(),
      mode,
      target: typeof flags.target === 'string' ? flags.target : undefined,
      gapId: typeof flags.gap === 'string' ? flags.gap : undefined,
      yes: flags.yes === true,
      noVerify: flags['no-verify'] === true,
    });
    console.log(JSON.stringify(result, null, 2));
    return result.status.startsWith('refused') || result.status === 'failed' ? 1 : 0;
  }

  if (!command && flags.verify === true && typeof flags.action === 'string') {
    return runVerifyActionCommand({
      cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd(),
      actionId: flags.action,
    });
  }

  if (!command && (isAgentMode || flags.verify === true || wantsJson || wantsJsonl || wantsStrict)) {
    const deferMachineOutput = wantsJson || wantsJsonl;
    const scanResult = await runScanCommand(flags, positional, { deferMachineOutput });

    if ((wantsJson || wantsJsonl) && !scanResult.artifacts) {
      console.error('VibeRaven could not produce machine output because scan artifacts were not written.');
      return 3;
    }

    const gateResult =
      scanResult.artifacts && (wantsJson || wantsJsonl || wantsStrict)
        ? (JSON.parse(await readFile(scanResult.artifacts.gateResultPath, 'utf8')) as GateResult)
        : undefined;
    const strictExitCode =
      wantsStrict && gateResult
        ? exitCodeForStrictGate(gateResult, { failOnWarnings: flags.strict === 'warning' })
        : scanResult.exitCode;

    if (wantsJson && gateResult) {
      process.stdout.write(renderGateResultJson(gateResult));
      return strictExitCode;
    }

    if (wantsJsonl && gateResult) {
      process.stdout.write(renderJsonlEvents(gateResult));
      return strictExitCode;
    }

    if (wantsStrict && gateResult) {
      return strictExitCode;
    }

    return scanResult.exitCode;
  }

  if (!command) {
    const mode = resolveDefaultEntrypointMode({
      stdinIsTTY: Boolean(process.stdin.isTTY),
      stdoutIsTTY: Boolean(process.stdout.isTTY),
      env: process.env
    });

    if (mode === 'local-ui') {
      const cwd = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();
      const handle = await startLocalUiServer({ cwd });
      console.log(`VibeRaven local UI: ${handle.url}`);
      await waitForServerShutdown();
      await handle.close();
      return 0;
    }

    if (mode === 'interactive') {
      await runInteractiveSession();
      return 0;
    }

    const scanResult = await runScanCommand({ 'agent-mode': true }, positional);
    return scanResult.exitCode;
  }



  switch (command) {

    case 'tui':

    case 'interactive':

      await runInteractiveSession();

      return 0;

    case 'ui': {
      const cwd = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();
      const envPort = Number(process.env.PORT);
      const handle = await startLocalUiServer({ cwd, port: Number.isFinite(envPort) && envPort > 0 ? envPort : undefined });
      console.log(`VibeRaven local UI: ${handle.url}`);
      await waitForServerShutdown();
      await handle.close();
      return 0;
    }

    case 'status':

      return cmdStatus(flags, positional);

    case 'check':
      return runCheckCommand({
        cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd(),
        json: Boolean(flags.json),
        quiet: Boolean(flags.quiet),
      });

    case 'fix': {
      const fixCwd = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();
      const gapId = typeof flags.gap === 'string' ? flags.gap : undefined;
      if (!gapId) {
        const artifact = await loadLastArtifact(fixCwd).catch(() => undefined);
        const fixable = artifact ? buildTaskList(artifact).filter((t) => t.fixType === 'repo-code' && !t.requiresUserAction) : [];
        if (fixable.length === 0) {
          console.log('Nothing to fix automatically. Run `viberaven check` first, then `viberaven fix --gap <id>`.');
          return artifact ? 0 : 1;
        }
        console.log('Fixable gaps:');
        for (const task of fixable) {
          console.log(`  viberaven fix --gap ${task.gapId}   ${task.title}`);
        }
        return 0;
      }
      const result = await runHealCommand({
        cwd: fixCwd,
        mode: 'apply',
        gapId,
        yes: true,
        noVerify: flags['no-verify'] === true,
      });
      console.log(JSON.stringify(result, null, 2));
      return result.status.startsWith('refused') || result.status === 'failed' ? 1 : 0;
    }

    case 'actions':
      return runActionsCommand({
        cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd(),
        json: Boolean(flags.json),
      });

    case 'preview':
      return runPreviewCommand({
        cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd(),
        agentMode: flags['agent-mode'] === true,
        json: Boolean(flags.json),
      });

    case 'next':

      return runNextCommand({
        json: Boolean(flags.json),
        cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd()
      });

    case 'guide': {
      const provider = positional[0];
      if (!provider) {
        console.error('Usage: viberaven guide <provider> [--step N] [--json]');
        return 1;
      }
      const stepRaw = flags.step;
      const step = typeof stepRaw === 'string' ? Number.parseInt(stepRaw, 10) : undefined;
      return runGuideCommand({
        provider,
        step: Number.isFinite(step) ? step : undefined,
        json: Boolean(flags.json)
      });
    }

    case 'open':
      return runOpenCommand({
        target: positional[0],
        cwd: process.cwd()
      });

    case 'scan': {

      const scanResult = await runScanCommand(flags, positional);
      return scanResult.exitCode;
    }

    case 'report':

      return cmdReport(flags, positional);

    case 'prompt':

      return cmdPrompt(flags, positional);

    case 'stack':

      return cmdStack(positional);

    case 'provider-verify':

      return cmdProviderVerify(flags, positional);

    case 'init': {
      const cwd = positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd();
      const agents = typeof flags.agents === 'string' ? flags.agents : undefined;
      return runInitCommand({
        cwd,
        agents,
        dryRun: flags['dry-run'] === true,
      });
    }

    case 'doctor':
      if (flags.agents !== true) {
        console.error('Usage: viberaven doctor --agents [path]');
        return 1;
      }
      return runDoctorAgentsCommand({
        cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd(),
      });

    case 'validate-npm-package':
      return runValidateNpmPackageCommand({
        names: positional,
        json: Boolean(flags.json),
      });

    case 'audit':
      if (flags['vercel-supabase'] !== true) {
        console.error('Usage: viberaven audit --vercel-supabase [--json] [path]');
        return 1;
      }
      return runAuditCommand({
        cwd: positional[0] ? resolve(process.cwd(), positional[0]) : process.cwd(),
        json: Boolean(flags.json),
      });

    default:

      console.error(`Unknown command: ${command}`);

      printHelp();

      return 1;

  }

}



if (require.main === module) {

  main()

    .then((code) => {

      if (code !== 0) {

        process.exitCode = code;

      }

    })

    .catch((error) => {

      console.error(error instanceof Error ? error.message : String(error));

      process.exitCode = 1;

    });

}
